import { type Observable, filter, take } from 'rxjs';

import { type WaitExpired, expireAfter } from './expireAfter.ts';

/**
 * Correlates a shared result$ stream by a caller-chosen key — a view
 * dispatches an action tagged with a fresh id, then awaits only the
 * response whose `key(result)` matches that same id, so concurrent
 * in-flight requests on the same stream never resolve each other's callers.
 *
 * The wait is bounded (`expireAfter`), and its expiry is an emitted value.
 */
export function correlatedResult$<R, K>(
  result$: Observable<R>,
  key: (result: R) => K,
  expected: K,
): Observable<R | WaitExpired> {
  return result$.pipe(
    filter((r) => key(r) === expected),
    take(1),
    expireAfter(),
  );
}

/** `correlatedResult$` keyed by the common `requestId` field. */
export function awaitByRequestId$<R extends { requestId: string }>(
  result$: Observable<R>,
  requestId: string,
): Observable<R | WaitExpired> {
  return correlatedResult$(result$, (r) => r.requestId, requestId);
}
